import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { BimWorkOrderOtherData, WorkOrderChangeObjectData } from 'gql/graphql';
import { SortEnumType, WorkOrderStatusEnum } from 'gql/graphql';
import { useTableCollapse } from 'hooks/useTableCollapse';
import {
    getBaseColumnsByContractType,
    useAddOtherToBimWorkOrderMenu,
} from 'pages/Project/BimProject/BimCommonComponents/BimAddOtherModal/';
import { getInitialChecked } from 'pages/Project/UerProject/WorkOrder/AddOrderOther/AddOthersTable';
import type { TableFilters, SelectedFilters } from 'uikit';
import { TableTextFilter } from 'uikit';

import type { RefObject } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { bimWorkOrderOtherForActGql, bimWorkOrderOthersGql } from '../bimAddOrderOtherQueries';

interface IUseAddOtherToBimWorkOrderTableData {
    workOrderId: number;
    constructionTypeId?: number;
    workOrderObjects: WorkOrderChangeObjectData[];
    isFrameContract: boolean;
    workOrderObjectId?: number;
    workOrderStatus?: WorkOrderStatusEnum;
    tableRef: RefObject<HTMLDivElement>;
}

const requestId = 'othersToBimWorkOrder';

export const useAddOtherToBimWorkOrderTableData = (props: IUseAddOtherToBimWorkOrderTableData) => {
    const dispatch = useDispatch();
    const {
        workOrderId,
        constructionTypeId,
        workOrderObjects,
        isFrameContract,
        workOrderObjectId,
        workOrderStatus,
        tableRef,
    } = props;

    const [createdOtherId, setCreatedOtherId] = useState<number | null>(null);
    const [currentFilters, setsCurrentFilters] = useState<SelectedFilters | null>(null);

    const isInProgress = useMemo(() => workOrderStatus === WorkOrderStatusEnum.InProgress, [workOrderStatus]);

    const variables: any = useMemo(() => {
        const order = [{ section: SortEnumType.Desc }, { name: SortEnumType.Asc }];

        if (isInProgress) {
            return workOrderObjectId ? { workOrderObjectId, order } : undefined;
        }

        return constructionTypeId ? { workOrderId, constructionTypeId, order } : undefined;
    }, [constructionTypeId, isInProgress, workOrderId, workOrderObjectId]);

    const [bimOtherToWorkOrderData, bimOtherToWorkOrderStatus, , bimOtherToWorkOrderRequest] = useRequest({
        requestId,
        query: bimWorkOrderOthersGql,
        variables,
        withEffect: !isInProgress && !!constructionTypeId,
    });

    const [bimWorkOrderOtherForActData, bimWorkOrderOtherForActStatus, , bimWorkOrderOtherForActRequest] = useRequest({
        requestId,
        query: bimWorkOrderOtherForActGql,
        variables,
        withEffect: isInProgress && !!variables,
    });

    const [resetOthersData] = useResetStoreData({
        requestId,
    });

    const refreshOthersDataAndSetCreatedId = useCallback(
        (newId: number) => {
            setCreatedOtherId(newId);
            dispatch(isInProgress ? bimWorkOrderOtherForActRequest(variables) : bimOtherToWorkOrderRequest(variables));
        },
        [bimOtherToWorkOrderRequest, bimWorkOrderOtherForActRequest, dispatch, isInProgress, variables],
    );

    const onFiltersUpdated = useCallback((selectedFilters: SelectedFilters) => {
        setsCurrentFilters(selectedFilters);
    }, []);

    useEffect(
        () => () => {
            resetOthersData(undefined);
        },
        [resetOthersData],
    );

    const items = useMemo(
        () =>
            isInProgress
                ? bimWorkOrderOtherForActData?.bimWorkOrderOtherForAct?.items
                : bimOtherToWorkOrderData?.bimWorkOrderOthers?.items,
        [
            bimOtherToWorkOrderData?.bimWorkOrderOthers?.items,
            bimWorkOrderOtherForActData?.bimWorkOrderOtherForAct?.items,
            isInProgress,
        ],
    );

    const othersWithConstructionTypes = useMemo(
        () =>
            items?.map((other) => ({
                ...other,
                rows: isInProgress
                    ? undefined
                    : workOrderObjects.map((workOrderObject) => ({
                          ...workOrderObject,
                          id: `${other.contractOtherCostId}_${workOrderObject.workOrderObjectId}` as string,
                          objectId: workOrderObject.workOrderObjectId,
                          parentId: other.contractOtherCostId,
                      })),
                id: other.contractOtherCostId,
            })) ?? [],
        [isInProgress, items, workOrderObjects],
    );

    const { menuColumn, checkedIds, setCheckedIds, setValueForCheckedRow, handleChangeCheckbox } =
        useAddOtherToBimWorkOrderMenu({
            othersWithConstructionTypes,
            disableCheckboxTooltip: isInProgress,
        } as any);

    const baseColumnsByContractType = useMemo(
        () =>
            getBaseColumnsByContractType<BimWorkOrderOtherData>({
                tableRef,
                checkedIds,
                valueSetter: setValueForCheckedRow,
                hideIncludeField: !isFrameContract || workOrderStatus === WorkOrderStatusEnum.InProgress,
            }),
        [checkedIds, isFrameContract, setValueForCheckedRow, tableRef, workOrderStatus],
    );

    const [preparedColumns, preparedRows, , collapseAll] = useTableCollapse(
        'addOtherToBimWorkOrder',
        baseColumnsByContractType,
        othersWithConstructionTypes,
        undefined,
    );

    const filteredRows = useMemo(() => {
        const passedIds: (string | number)[] = [];
        const hasFilter = currentFilters?.name.selected.includes('name') && currentFilters?.name.value;

        return hasFilter
            ? preparedRows.filter((row) => {
                  const isPassedFilters = row.parentId
                      ? passedIds.includes(row.parentId)
                      : currentFilters?.name.value.findIndex(
                            ({ value }: { value: string; name: string }) => row.name === value,
                        ) !== -1;

                  if (isPassedFilters && typeof row.contractOtherCostId !== 'undefined') {
                      passedIds.push(row.contractOtherCostId);
                  }

                  return isPassedFilters;
              })
            : preparedRows;
    }, [currentFilters?.name.selected, currentFilters?.name.value, preparedRows]);

    const finalColumns = useMemo(() => [menuColumn, ...preparedColumns], [menuColumn, preparedColumns]);

    useEffect(() => {
        if (othersWithConstructionTypes.length && !Object.keys(checkedIds).length && !isInProgress) {
            const initialChecked = !!othersWithConstructionTypes && getInitialChecked(othersWithConstructionTypes);

            setCheckedIds(initialChecked);
            collapseAll()();
        }
    }, [checkedIds, collapseAll, isInProgress, othersWithConstructionTypes, preparedRows, setCheckedIds]);

    const isTableLoaded = useMemo(
        () =>
            isInProgress
                ? bimWorkOrderOtherForActStatus === RequestStatus.SUCCESS
                : bimOtherToWorkOrderStatus === RequestStatus.SUCCESS,
        [bimOtherToWorkOrderStatus, bimWorkOrderOtherForActStatus, isInProgress],
    );

    const tableFilters = useMemo<TableFilters<(typeof preparedRows)[number]>>(
        () => [
            {
                id: 'name',
                name: 'Наименование: ',
                field: 'name',
                filterer: () => true,
                component: {
                    name: TableTextFilter,
                    props: {
                        withSearch: true,
                        items: othersWithConstructionTypes.map((item: { name: string }) => ({
                            name: item.name,
                            value: item.name,
                        })),
                    },
                },
            },
        ],
        [othersWithConstructionTypes],
    );

    useEffect(() => {
        if (isTableLoaded && createdOtherId) {
            const row = preparedRows.find((otherRow) => Number(otherRow.id) === createdOtherId);

            if (row) {
                handleChangeCheckbox(row)({ checked: true });
                setCreatedOtherId(null);
            }
        }
    }, [createdOtherId, handleChangeCheckbox, isTableLoaded, preparedRows]);

    const haveCheckedOthers = Object.values(checkedIds).some((checkedId) => checkedId.parent);

    return {
        tableRows: filteredRows,
        tableColumns: finalColumns,
        tableFilters,
        fetchStatus: bimOtherToWorkOrderStatus,
        haveCheckedOthers,
        checkedIds,
        refreshOthersDataAndSetCreatedId,
        onFiltersUpdated,
    };
};
